import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackMarketingEvent } from '../lib/analytics';
import { resolveCrmRegisterUrl } from '../lib/appUrls';
import { scrollToDemoForm } from '../lib/navigation';
import { homeButtonClasses } from './home/HomeButton';
import { cn } from '../lib/utils';

const SCROLL_VISIBILITY_THRESHOLD = 400;

export function StickyMobileCta(): React.JSX.Element {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const isHomePage = location.pathname === '/';
  const crmRegisterUrl = resolveCrmRegisterUrl(
    import.meta.env.VITE_CRM_REGISTER_URL,
    import.meta.env.VITE_CRM_LOGIN_URL
  );

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    setIsVisible(scrollY > SCROLL_VISIBILITY_THRESHOLD);

    const formEl = document.getElementById('demo-form');
    if (formEl) {
      const rect = formEl.getBoundingClientRect();
      setIsFormVisible(rect.top < window.innerHeight && rect.bottom > 0);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (!isVisible || isFormVisible) {
    return <></>;
  }

  return (
    <div className="marketing-mobile-cta safe-bottom fixed bottom-0 left-0 right-0 z-50 border-t px-4 py-3 backdrop-blur-xl md:hidden">
      <div className="flex items-center gap-3">
        <a
          href={crmRegisterUrl}
          onClick={() => {
            trackMarketingEvent('hero_cta_click', {
              location: 'sticky_mobile_bar',
              cta: 'free_company_register',
            });
          }}
          className={cn(
            'flex-1 py-3 text-center text-sm font-semibold transition-colors',
            isHomePage
              ? homeButtonClasses('accent', 'sm')
              : 'blueprint-border bg-brand-accent text-white hover:bg-brand-accent/90'
          )}
        >
          {t('cta.freePlanSignup')}
        </a>
        <button
          type="button"
          onClick={() => {
            trackMarketingEvent('hero_cta_click', {
              location: 'sticky_mobile_bar',
              cta: 'request_demo',
            });
            if (isHomePage) {
              scrollToDemoForm();
              return;
            }

            navigate({ pathname: '/', hash: '#demo-form' });
          }}
          className={cn(
            'px-4 py-3 text-sm font-semibold transition-colors',
            isHomePage
              ? 'rounded-full border border-[var(--ir-line)] text-[var(--ir-ink)] hover:bg-white'
              : 'border border-brand-primary/15 text-brand-primary hover:bg-brand-bg'
          )}
        >
          {t('cta.requestDemo')}
        </button>
      </div>
    </div>
  );
}
